// ProductQuantity.tsx
import * as React from "react";
import styled, { css } from "styled-components";
import { useDebounce } from "use-debounce";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import Icon from "src/library/components/Icon/Icon";
import { Text } from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { Icons } from "src/library/components/Icon/types";

export interface IProductQuantityProps {
  initialQuantity?: number;
  handleQuantityUpdate: (newQuantity: number) => void;
  canRemove: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
  layout?: "standard" | "rapid";
  onBlur?: () => void;
  onFocus?: () => void;
}

export interface ProductQuantityRef {
  inputRef: HTMLInputElement | null;
  focus: () => void;
  quantity: number;
}

const ProductQuantity: React.ForwardRefRenderFunction<unknown, IProductQuantityProps> = ({
  initialQuantity = 0,
  hasError,
  autoFocus,
  canRemove,
  onBlur,
  onFocus,
  handleQuantityUpdate,
  layout = "standard"
}, ref) => {
  const [ count, setCount ] = React.useState(initialQuantity || 0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [ isFocused, setFocused ] = React.useState(false);
  const [ countDebounce ] = useDebounce(count, 250);
  const [ shouldEmit, setShouldEmit ] = React.useState(false);

  React.useEffect(() => {
    setCount(currCount => {
      setShouldEmit(false);

      if (currCount !== initialQuantity) {
        return initialQuantity;
      }

      return currCount;
    });
  }, [ initialQuantity ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target) {
      const value = e.target.value;

      setShouldEmit(true);

      setCount(currentCount => {
        const valueInt = value === "" ? 0 : parseInt(value);
        let newValue = valueInt;

        // accept 0 locally for editing purposes. Actual 0 update handled on input onBlur event
        if (valueInt === 0) {
          newValue = 0;
        } else if (currentCount === valueInt) {
          // captures instance where count was 0 before quantity updated
          newValue = currentCount;
        } else if (valueInt > 0) {
          // an update of 0 handled on input onBlur event
          newValue = valueInt;
        }

        return newValue;
      });
    }
  };

  React.useEffect(() => {
    if (shouldEmit) {
      if (countDebounce !== initialQuantity) {
        handleQuantityUpdate(countDebounce);
      }
    }
  }, [
    countDebounce,
    shouldEmit,
    handleQuantityUpdate,
    initialQuantity
  ]);

  const handleIncrement = React.useCallback(() => {
    setCount(curr => parseFloat((curr + 1).toFixed(2)));
    setShouldEmit(true);
  }, []);

  const handleDecrement = () => {
    setShouldEmit(true);

    setCount((currentCount => {
    // stop update to quantity if result 0. Handling on input onBlur event
      if ((count - 1) <= 0) {
        return 0;
      }

      if (count > 0) {
        return parseFloat((count - 1).toFixed(2));
      }

      return currentCount;
    }));
  };

  React.useEffect(() => {
    if (autoFocus) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [ autoFocus ]);

  React.useImperativeHandle<unknown, ProductQuantityRef>(ref, () => {
    return {
      inputRef: inputRef.current,
      quantity: count || 0,
      focus: () => {
        if (count) {
          inputRef.current?.focus();
        } else {
          setCount(1);
          inputRef.current?.focus();
        }
      }
    };
  }, [ count ]);

  return (
    <Grid.Bounds
      direction="horizontal"
      valign="center"
      className="quantity-buttons"
    >
      {!!canRemove && !!count && (
        <IconWrapper
          onClick={() => {
            setShouldEmit(true);
            setCount(0);
          }}
        >
          <Icon
            size={20}
            icon={Icons.CLOSECIRCLE}
            $fillColor="lightGrey"
            cursor="pointer"
          />
        </IconWrapper>
      )}

      <ProductQuantityWrapper
        className={`layout-${layout}`}
        quantity={count || 0}
      >
        {hasError ? (
          <Text>
            {count}
          </Text>
        ) : layout === "rapid" ?
          (
            <>
              {!!count && count > 0 ? (
                <>
                  <QuantityField
                    min={0}
                    ref={inputRef}
                    quantity={count || 0}
                    value={count}
                    onChange={handleChange}
                    autoFocus
                    onFocus={e => {
                      if (e.target.value) {
                        setCount(parseInt(e.target.value, 10));
                        setShouldEmit(true);
                      }

                      onFocus && onFocus();
                      setFocused(true);
                      e.target.select();
                    }}
                    onBlur={() => {
                      onBlur && onBlur();

                      setTimeout(() => {
                        setFocused(false);
                      }, 100);

                      // only action 0 update if local state 0 when user leaves component
                      if (inputRef.current?.value === "") {
                        setShouldEmit(true);
                        setCount(1);
                      }
                    }}
                  />

                  {isFocused ? (
                    <Button className="rapid-done-button">
                      Done
                    </Button>
                  ) : (
                    <Button
                      className="rapid-edit-button"
                      onClick={() => {
                        inputRef.current?.focus();
                        handleQuantityUpdate(1);
                        handleIncrement();
                      }}
                    >
                      Edit qty
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  onClick={() => {
                    setCount(1);
                    setShouldEmit(true);
                  }}
                >
                  Add to cart
                </Button>
              )}

            </>
          ) :
          (
            <>
              <QuantityButton
                className="incrementer left"
                onClick={() => handleDecrement()}
              >
                -
              </QuantityButton>

              <QuantityField
                min={0}
                ref={inputRef}
                quantity={count || 0}
                value={count === 0 ? "" : count}
                onChange={handleChange}
                onFocus={e => {
                  onFocus && onFocus();
                  e.target.select();
                }}
                onBlur={() => {
                  onBlur && onBlur();

                  // only action 0 update if local state 0 when user leaves component
                  if (inputRef.current?.value === "") {
                    setCount(1);
                  }
                }}
              />

              <QuantityButton
                className="incrementer right"
                onClick={() => handleIncrement()}
              >
                +
              </QuantityButton>
            </>
          )}
      </ProductQuantityWrapper>
    </Grid.Bounds>
  );
};

const IconWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    flex-direction: row;
    line-height: 1em;
    position: relative;
    top: 1px;

    ${breakpoint("sm", theme)} {
      display: block;
    }
    margin-right: ${theme.spacing.margins.sm};
  `}
`;

const ProductQuantityWrapper = styled.div<{ quantity: number }>`
  ${({ quantity, theme }) => css`
    height: 39px;
    display: flex;
    align-items: center;

    &.layout-rapid {      
      ${QuantityField} {
        width: calc(40px + ${quantity.toString().length * 7}px); // dynamic get bigger
        border-right: none;
        border-left: 1px solid ${theme.colors.lightGrey};
        border-top-left-radius: ${theme.borderRadius};
        border-bottom-left-radius: ${theme.borderRadius};
      }

      .rapid-done-button, .rapid-edit-button {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }

    &.layout-standard {
      ${QuantityField} {
        border-right: none;
        border-left: none;
      }
    }
  `}
`;

const QuantityField = styled.input.attrs({ type: "number" })<{ quantity: number }>`
  ${({ theme, quantity }) => css`
    appearance: none;
    height: 100%;
    padding: 0;
    vertical-align: top;
    width: calc(20px + ${quantity.toString().length * 7}px); // dynamic get bigger
    font-size: 12px; // hard coded as needs to not change
    border-radius: 0;
    text-align: center;
    border-top: 1px solid ${theme.colors.lightGrey};
    border-bottom: 1px solid ${theme.colors.lightGrey};
    outline: none;
    padding-left: 12px;

    input {
      border: none;
    }

    &:focus,
    &:hover {
      color: ${theme.colors.primaryDark};
    }

    ${breakpoint("sm", theme)} {
      font-size: ${theme.fonts.sizes.p};
      width: calc(38px + ${quantity.toString().length * 7}px); // dynamic get bigger
    }

    @media (hover: none) and (pointer: coarse) {
      padding-left: 0px;
    }
  `}
`;

const QuantityButton = styled(Button)`
  ${({ theme }) => css`
    line-height: 1em;
    height: 39px;
    margin: 0;
    padding: 0 10px;
    display: block;

    &.incrementer {
      background-color: ${theme.colors.white};
      color: ${theme.colors.primaryLight};
      border: 1px solid ${theme.colors.lightGrey};
      min-width: 30px;
    }

    &.left {
      border-radius: 8px 0 0 8px;
    }
    &.right {
      border-radius: 0 8px 8px 0;
    }

    ${breakpoint("sm", theme)} {
     
    }
  `};
`;

export default React.memo(React.forwardRef(ProductQuantity));
